import {
  Box,
  Button,
  ButtonProps,
  Flex,
  FormControl,
  FormErrorMessage,
  Heading,
  Image,
  Input,
  Link,
  Text,
  VisuallyHidden,
} from '@everlywell/ui-kit';
import DynamicIcon from 'components/DynamicIcon';
import SectionContainer from 'components/PageSections/components/SectionContainer';
import { createSharedButtonProps } from 'components/PageSections/utils';
import Stepper from 'components/Stepper';
import { Link as GatsbyLink } from 'gatsby';
import { GatsbyImage as Img } from 'gatsby-plugin-image';
import React, { useState } from 'react';
import analytics from 'utils/analytics';
import { sendNotificationEvent } from 'utils/api/subscriptionsApi';
import { ANALYTICS } from 'utils/constants/analytics';
import useMdpModals from 'utils/hooks/useMdpModals';
import { IGatsbyImage } from 'utils/types';
import { User } from 'utils/types';

// Types ----------

export interface QuizQuestionOptionProps {
  title: string;
  image?: IGatsbyImage;
}

export interface QuizQuestionProps {
  options: QuizQuestionOptionProps[];
  // Add more types in the future if needed (e.g. openField, slider, etc.)
  quizQuestionType: string;
  title: string;
  eyebrow?: string;
}

export interface QuizEmailCaptureProps {
  title: string;
  cta?: {
    label: string;
    url: string;
  };
}

export interface QuizRightSectionProps {
  eyebrow: string;
  title: string;
  cta: {
    label: string;
    url: string;
  };
  backgroundImage?: IGatsbyImage;
}

export interface QuizProps {
  questions: QuizQuestionProps[];
  quizEmailCapture: QuizEmailCaptureProps;
  rightSection?: QuizRightSectionProps;
}

interface UserSelectionProps {
  question: string;
  answer: string | string[];
}

// Quiz ------

const Quiz: React.FC<QuizProps> = (props) => {
  const { questions, quizEmailCapture, rightSection } = props;
  // isEmailQuizEnabled flag removed - defaulted to true
  const isEmailQuizEnabled = true;
  const { handleMdpCtaClick } = useMdpModals();

  // Initialize user selections with question title and blank answers
  const blankUserSelections = questions.map((question) => {
    if (question.quizQuestionType === 'multiSelect') {
      return {
        question: question.title,
        answer: [],
      };
    }

    return {
      question: question.title,
      answer: '',
    };
  });

  const [userSelections, setUserSelections] =
    useState<UserSelectionProps[]>(blankUserSelections);
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [isQuizCompleted, setIsQuizCompleted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [email, setEmail] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  if (!isEmailQuizEnabled) {
    return null;
  }

  const steps = questions.map((_, index) => ({ title: `0${index + 1}` }));

  /**
   * Handle Next question click
   * @param currentIndex
   */
  const handleNext = () => {
    if (activeStepIndex <= questions.length - 2) {
      setActiveStepIndex((prevState) => prevState + 1);

      analytics.track({
        event: ANALYTICS.EVENTS.CLICKED_QUIZ_PROCEED,
        data: {
          questionIndex: activeStepIndex,
          questionTitle: questions[activeStepIndex].title,
        },
      });
    } else {
      // Move to email capture module
      setIsQuizCompleted(true);
    }
  };

  /**
   * Handle Previous question click
   */
  const handlePrev = () => {
    if (activeStepIndex > 0) {
      setActiveStepIndex((prevState) => prevState - 1);

      analytics.track({
        event: ANALYTICS.EVENTS.CLICKED_QUIZ_BACK,
        data: {
          questionIndex: activeStepIndex,
          questionTitle: questions[activeStepIndex].title,
        },
      });
    }
  };

  /**
   * Handle Quiz Question option selection
   * @param quizQuestionType
   * @param index
   * @param value
   */
  const handleOptionSelection = (quizQuestionType: string, value: string) => {
    const newSelectedOptions = [...userSelections];

    const updateSingeSelect = (value: string) => {
      const currentAnswer = newSelectedOptions[activeStepIndex].answer;
      newSelectedOptions[activeStepIndex].answer =
        currentAnswer === value ? '' : value;
    };

    const updateMultiSelect = (value: string) => {
      let currentAnswer = newSelectedOptions[activeStepIndex].answer;

      if (Array.isArray(currentAnswer)) {
        if (currentAnswer.includes(value)) {
          currentAnswer = currentAnswer.filter((answer) => answer !== value);
        } else {
          currentAnswer.push(value);
        }
        newSelectedOptions[activeStepIndex].answer = currentAnswer;
      }
    };

    if (quizQuestionType === 'singleSelect') {
      updateSingeSelect(value);
    } else if (quizQuestionType === 'multiSelect') {
      updateMultiSelect(value);
    }

    setUserSelections(newSelectedOptions);

    analytics.track({
      event: ANALYTICS.EVENTS.CLICKED_QUIZ_OPTION,
      data: {
        questionIndex: activeStepIndex,
        questionTitle: questions[activeStepIndex].title,
        label: value,
      },
    });
  };

  /**
   * Handle Quiz Submission
   */
  const handleQuizSubmit: React.FormEventHandler<HTMLFormElement> = async (
    event,
    user = {} as User,
  ) => {
    event.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');
    setIsSubmitting(true);

    if (!email) {
      setErrorMessage('Email is required.');
      setIsSubmitting(false);
      return;
    }

    try {
      sendNotificationEvent({
        event_type: 'newsletter-subscription',
        resource: {
          user_id: user.id,
          email: email,
          data_fields: {
            quiz: userSelections,
            newsletter: 'weight-care-plus',
          },
        },
      });
      setSuccessMessage('Thank you, we will get in contact soon.');

      analytics.track({
        event: ANALYTICS.EVENTS.SUBMITTED_QUIZ,
        data: {
          label: 'Submit',
          quizProgram: 'weight-care-plus',
        },
      });
    } catch (error) {
      setErrorMessage('Email submission failed, please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Handle Quiz Reset
   */
  const handleQuizReset = () => {
    setUserSelections(blankUserSelections);
    setActiveStepIndex(0);
    setIsQuizCompleted(false);
    setEmail('');
    setErrorMessage('');
    setSuccessMessage('');

    analytics.track({
      event: ANALYTICS.EVENTS.CLICKED_QUIZ_RESET,
      data: {
        label: 'Reset',
        quizProgram: 'weight-care-plus',
      },
    });
  };

  const handleRightSectionButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    trackRightSectionCtaClick();

    handleMdpCtaClick({
      event,
      ctaUrl: rightSection?.cta?.url || '',
    });
  };

  const trackRightSectionCtaClick = () => {
    analytics.track({
      event: ANALYTICS.EVENTS.CLICKED_LINK,
      data: {
        sectionHeader: 'Email Quiz',
        contentfulSection: 'EmailQuiz',
        ctaText: rightSection?.cta?.label || 'No label provided',
      },
    });
  };

  const rightSectionButtonProps: ButtonProps = {
    onClick: handleRightSectionButtonClick,
    id: 'email-quiz-cta',
    ...createSharedButtonProps(rightSection?.cta?.url),
  };

  return (
    <SectionContainer size={['sm', 'sm', 'lg', 'xl']}>
      <Box
        backgroundColor="tints.lightCream"
        borderRadius="3xl"
        justifyContent="space-between"
        overflow="hidden"
        padding="4"
        width="100%"
        height="auto"
        display={['block', null, 'flex']}
      >
        {/* Left Question Area */}
        <Box
          alignContent={
            !isQuizCompleted
              ? 'space-between'
              : quizEmailCapture.cta
              ? 'space-between'
              : 'flex-end'
          }
          backgroundColor="#E6ADFF60"
          borderRadius="xl"
          display={['block', null, 'flex']}
          flexWrap="wrap"
          marginBottom={['4', null, 0]}
          minHeight={['auto', null, '490px']}
          padding="8"
          width={['100%', null, rightSection ? 'calc(70% - 8px)' : '100%']}
        >
          {!isQuizCompleted ? (
            <Stepper steps={steps} activeStepIndex={activeStepIndex} />
          ) : quizEmailCapture.cta ? (
            <Box>
              <Link
                as={GatsbyLink}
                size="lg"
                color="eggplant.light"
                to={quizEmailCapture.cta.url}
              >
                {quizEmailCapture.cta.label}
              </Link>
            </Box>
          ) : null}

          <Box width="100%" marginTop={['16', null, 0]}>
            {questions.map((question, index) => (
              <Box
                key={`quiz-question-${index}`}
                width="100%"
                flexWrap="wrap"
                display={
                  index === activeStepIndex && !isQuizCompleted
                    ? 'flex'
                    : 'none'
                }
              >
                <Box width="400px" marginBottom="8">
                  <Heading
                    size="xl"
                    color="eggplant.base"
                    opacity="0.6"
                    maxWidth="100%"
                    display="block"
                  >
                    {question.eyebrow}
                  </Heading>
                  <Heading
                    size="xl"
                    color="tints.black"
                    maxWidth="100%"
                    display="block"
                  >
                    {question.title}
                  </Heading>
                </Box>

                <Flex width="100%" marginBottom="5" flexWrap="wrap">
                  {question.options.map((option, optionIndex) => {
                    const isSelected = userSelections[
                      activeStepIndex
                    ].answer.includes(option.title);

                    return (
                      <Button
                        border="4px solid"
                        borderColor={
                          isSelected ? 'eggplant.lighter' : 'transparent'
                        }
                        backgroundColor="eggplant.wash"
                        colorScheme="whiteCS"
                        display="flex"
                        key={`quiz-option-${index}-${optionIndex}`}
                        data-testid={
                          isSelected
                            ? 'quiz-question-option-selected'
                            : 'quiz-question-option'
                        }
                        marginBottom="3"
                        marginRight={['10px', null, '4']}
                        size="md"
                        variant="primary"
                        // Reducing the padding to account for the "selected" border of 4px
                        // important is the only way to override the default padding
                        padding="8px 12px !important"
                        _last={{
                          marginRight: 0,
                        }}
                        _hover={{
                          cursor: ['default', null, 'pointer'],
                        }}
                        onClick={() =>
                          handleOptionSelection(
                            question.quizQuestionType,
                            option.title,
                          )
                        }
                        position="relative"
                      >
                        {option.image && (
                          <Box position="absolute" top="0" left="0">
                            <Image
                              as={Img}
                              image={option.image.gatsbyImageData}
                              alt={option.title}
                              objectFit="fill"
                              width={['40px', null, '45px']}
                              height={['40px', null, '45px']}
                              display="block"
                            />
                          </Box>
                        )}
                        <Heading
                          size="sm"
                          as="span"
                          paddingLeft={option.image ? '32px' : '0'}
                        >
                          {option.title}
                        </Heading>
                      </Button>
                    );
                  })}
                </Flex>

                {activeStepIndex > 0 && (
                  <Button
                    marginRight={['10px', null, '4']}
                    onClick={() => handlePrev()}
                    isDisabled={activeStepIndex === 0}
                    _hover={{
                      cursor: ['default', null, 'pointer'],
                    }}
                    key={`quiz-back-${index}`}
                    data-testid="quiz-back-button"
                    variant="secondary"
                    colorScheme="greenCS"
                  >
                    Back
                  </Button>
                )}

                <Button
                  isDisabled={userSelections[index].answer.length === 0}
                  onClick={() => handleNext()}
                  _hover={{
                    cursor: ['default', null, 'pointer'],
                  }}
                  data-testid="quiz-proceed-button"
                  key={`quiz-proceed-${index}`}
                >
                  Proceed
                </Button>
              </Box>
            ))}

            {/* Email Capture Step after quiz */}
            <Box
              key="quiz-email-capture"
              display={isQuizCompleted ? 'block' : 'none'}
              width="100%"
            >
              <Box width="100%" marginBottom="8">
                <Heading
                  size="xl"
                  color="tints.black"
                  display="block"
                  marginBottom={0}
                  width="100%"
                  as="span"
                  maxWidth="500px"
                >
                  {quizEmailCapture.title}
                </Heading>
              </Box>

              <Box>
                <form
                  onSubmit={handleQuizSubmit}
                  data-testid="email-capture-form"
                >
                  <FormControl isInvalid={!!errorMessage}>
                    {errorMessage && (
                      <FormErrorMessage>{errorMessage}</FormErrorMessage>
                    )}
                    <Flex gap={3} alignItems="center">
                      {/* VisuallyHidden used to announce the success message to screen readers*/}
                      <VisuallyHidden aria-live="polite" aria-atomic="true">
                        {successMessage}
                      </VisuallyHidden>
                      {/* Displayed after submitting */}
                      {successMessage ? (
                        <>
                          <Flex
                            flexWrap="nowrap"
                            justifyContent="space-between"
                            width="100%"
                          >
                            <Flex flexWrap="nowrap" alignItems="center">
                              <DynamicIcon
                                icon="CheckCircle"
                                color="eggplant.light"
                                weight="fill"
                                marginRight="2"
                              />
                              <Text color={'eggplant.light'} size="xl">
                                {successMessage}
                              </Text>
                            </Flex>

                            <Button
                              variant="primary"
                              type="submit"
                              size="md"
                              colorScheme="whiteCS"
                              onClick={() => handleQuizReset()}
                            >
                              Got it
                            </Button>
                          </Flex>
                        </>
                      ) : (
                        <>
                          <Input
                            aria-label="Email address"
                            placeholder="Type your email"
                            name="subscribeEmail"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            isDisabled={isSubmitting}
                          />
                          {/* Submit */}
                          <Button
                            variant="primary"
                            type="submit"
                            size="md"
                            isLoading={isSubmitting}
                            // Making it match the height of the input box
                            // important is the only way to override the default padding
                            padding="12px 14px !important"
                            data-testid="quiz-email-submit-button"
                          >
                            Submit
                          </Button>
                        </>
                      )}
                    </Flex>
                  </FormControl>
                </form>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Right Image Area */}
        {rightSection ? (
          <Flex
            borderRadius="xl"
            height={['345px', null, '490px']}
            overflow="hidden"
            position="relative"
            width={['100%', null, 'calc(30% - 8px)']}
          >
            {/* Background Gradient */}
            <Box
              background="linear-gradient(254deg, rgba(0, 0, 0, 0.00) 12%, rgba(0, 0, 0, 0.60) 55%)"
              height="100%"
              left="0"
              position="absolute"
              top="0"
              width="100%"
              zIndex={1}
            />

            {/* Background Image */}
            {rightSection.backgroundImage && (
              <Box
                data-testid="background-image"
                height="100%"
                left="0"
                position="absolute"
                top="0"
                width="100%"
                zIndex={0}
              >
                <Img
                  style={{
                    height: '100%',
                    width: '100%',
                  }}
                  image={rightSection.backgroundImage.gatsbyImageData}
                  alt={rightSection.title}
                />
              </Box>
            )}

            {/* Content */}
            <Flex
              alignContent="space-between"
              flexWrap="wrap"
              padding="8"
              position="relative"
              width="100%"
              zIndex={2}
            >
              <Text size="lg" color="viridian.light">
                {rightSection.eyebrow}
              </Text>

              <Box width="100%">
                <Heading size="lg" color="tints.white">
                  {rightSection.title}
                </Heading>
                <Button
                  {...rightSectionButtonProps}
                  marginTop="4"
                  size="md"
                  variant="primary"
                  _hover={{
                    cursor: ['default', null, 'pointer'],
                  }}
                >
                  {rightSection.cta.label}
                </Button>
              </Box>
            </Flex>
          </Flex>
        ) : null}
      </Box>
    </SectionContainer>
  );
};

// Export ---------

export default React.memo(Quiz);
